import React from 'react';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { useTheme, DARK_MODE_ENABLED } from '../../contexts/ThemeContext';

interface ThemeToggleProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'button' | 'switch';
  showLabel?: boolean;
}

export function ThemeToggle({ 
  size = 'md', 
  variant = 'button',
  showLabel = false 
}: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();

  // BugHerd #144: the dark theme is unfinished and fails WCAG AA contrast, so no control
  // is offered to switch into it. Rendering nothing keeps every call site gated by the
  // single flag rather than by a condition repeated at each of them.
  if (!DARK_MODE_ENABLED) {
    return null;
  }

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (variant === 'switch') {
    return (
      <div className="flex items-center space-x-3">
        {showLabel && (
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {theme === 'light' ? 'Light' : 'Dark'} Mode
          </span>
        )}
        <button
          onClick={toggleTheme}
          className={`
            relative inline-flex items-center justify-center
            ${sizeClasses[size]} rounded-full
            bg-gray-200 dark:bg-gray-700
            transition-colors duration-200
            hover:bg-gray-300 dark:hover:bg-gray-600
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
          `}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          <div className={`
            absolute inset-0 flex items-center justify-center
            transition-opacity duration-200
            ${theme === 'light' ? 'opacity-100' : 'opacity-0'}
          `}>
            <SunIcon className={`${iconSizeClasses[size]} text-yellow-500`} />
          </div>
          <div className={`
            absolute inset-0 flex items-center justify-center
            transition-opacity duration-200
            ${theme === 'dark' ? 'opacity-100' : 'opacity-0'}
          `}>
            <MoonIcon className={`${iconSizeClasses[size]} text-blue-400`} />
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3">
      {showLabel && (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {theme === 'light' ? 'Light' : 'Dark'} Mode
        </span>
      )}
      <button
        onClick={toggleTheme}
        className={`
          ${sizeClasses[size]} rounded-lg
          bg-white dark:bg-gray-800
          border border-gray-300 dark:border-gray-600
          shadow-sm hover:shadow-md
          transition-all duration-200
          hover:bg-gray-50 dark:hover:bg-gray-700
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
          flex items-center justify-center
          group
        `}
        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      >
        {theme === 'light' ? (
          <MoonIcon className={`${iconSizeClasses[size]} text-gray-600 group-hover:text-blue-600 transition-colors`} />
        ) : (
          <SunIcon className={`${iconSizeClasses[size]} text-yellow-400 group-hover:text-yellow-500 transition-colors`} />
        )}
      </button>
    </div>
  );
}

export default ThemeToggle;